// userContext.tsx - Contexto global do usuário
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface UserData {
    id: number;
    name: string;
    email: string;
    cargo: string;
    account_id: number;
    foto_perfil_url?: string; // Nova propriedade para URL da foto
}

interface UserContextType {
    userData: UserData | null;
    updateUserData: (newData: Partial<UserData>) => void;
    clearUserData: () => void;
    refreshUserData: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser deve ser usado dentro de um UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [userData, setUserData] = useState<UserData | null>(null);

    // Função para carregar dados do localStorage
    const loadUserData = () => {
        try {
            const userDataString = localStorage.getItem('user');
            if (userDataString) {
                const parsedUserData: UserData = JSON.parse(userDataString);
                setUserData(parsedUserData);
            }
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
            setUserData(null);
        }
    };

    // Função para atualizar dados do usuário
    const updateUserData = (newData: Partial<UserData>) => {
        setUserData(prevData => {
            if (!prevData) return null;

            const updatedData = { ...prevData, ...newData };
            // Salva no localStorage
            localStorage.setItem('user', JSON.stringify(updatedData));
            return updatedData;
        });
    };

    // Função para limpar dados do usuário
    const clearUserData = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUserData(null);
    };

    // Função para recarregar dados do localStorage
    const refreshUserData = () => {
        loadUserData();
    };

    // Carrega dados iniciais
    useEffect(() => {
        loadUserData();
    }, []);

    // Escuta mudanças no localStorage (para sincronizar entre abas)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'user') {
                loadUserData();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    return (
        <UserContext.Provider value={{
            userData,
            updateUserData,
            clearUserData,
            refreshUserData
        }}>
            {children}
        </UserContext.Provider>
    );
};